import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/* Dynamsoft */
import { initializeDynamsoft } from '@app/dynamsoft.config';
import { BarcodeFormat } from '@src/app/shared/enums/BarcodeFormat.enum';
import { CameraEnhancer, CameraView, Feedback, CaptureVisionRouter, MultiFrameResultCrossFilter } from 'dynamsoft-barcode-reader-bundle';

import { ScannerCameraStatus } from '@app/shared/enums/ScannerCameraStatus.enum';
const ERROR_DESTROYED = 'Multi-scanner destroyed';

export interface InventoryMultiScannerItem {
  id: string;
  serialNumber: string;
  scannedAt: Date;
  source: 'camera' | 'manual';
  format?: string;
}

@Component({
    selector: 'app-multi-scanner',
    templateUrl: './multi-scanner.component.html',
    styleUrls: ['./multi-scanner.component.scss'],
    standalone: false
})
export class MultiScannerComponent implements AfterViewInit, OnDestroy {

  @ViewChild('uiWrapper') uiWrapper?: ElementRef<HTMLDivElement>;

  @Input() title = 'Inventory scanner';
  @Input() autoStart = false; // false -> display start-guide
  @Input() showCameraSelector = false;
  @Input() startDrawerOpen = false;
  @Input() openDrawerOnScan = false;
  @Input() maxItems?: number; // max items per scan session
  @Input() initialItems: string[] = []; // reopen scanner with already scanned items

  /* https://www.dynamsoft.com/capture-vision/docs/web/programming/javascript/api-reference/capture-vision-router/enum-preset-template.html */
  @Input() captureTemplateName = 'ReadSingleBarcode';

  // Dynamsoft format mask
  @Input() barcodeFormatIds: bigint = BigInt(BarcodeFormat.BF_QR_CODE);

  // Dynamsoft duplicate forget time (frame level)
  @Input() allowDuplicateScans = false;
  @Input() duplicateForgetTime = 5000;

  @Output() itemsChange = new EventEmitter<InventoryMultiScannerItem[]>();
  @Output() itemScanned = new EventEmitter<InventoryMultiScannerItem>();
  @Output() duplicateScanned = new EventEmitter<string>();
  @Output() finishScanning = new EventEmitter<InventoryMultiScannerItem[]>();
  @Output() scannerClosed = new EventEmitter<void>();
  @Output() scannerError = new EventEmitter<any>();

  private markScannerAsReady?: () => void;
  private scannerReady: Promise<void> = new Promise(resolve => {
    this.markScannerAsReady = resolve;
  });

  private bDestroyed = false;
  private isCapturing = false;
  private pendingCameraOperation: Promise<void> = Promise.resolve();
  private seenSerialNumbers = new Set<string>();

  // Dynamsoft scanner objects
  cvRouter?: CaptureVisionRouter;
  cameraEnhancer?: CameraEnhancer;
  cameraView?: CameraView;
  cameraStatus = ScannerCameraStatus.IDLE;

  camerasList: any[] = [];
  selectedCameraId = '';

  drawerOpen = false;
  manualEntryOpen = false;
  manualSerialNumber = '';
  manualEntryError = '';

  scannedItems: InventoryMultiScannerItem[] = [];
  scannedItemsPaginatorFirst = 0;
  scannedItemsPaginatorRows = 10;

  // flags to ignore repeated reads of the same QR while on camera
  private lastCameraScanValue = '';
  private lastCameraScanAt = 0;
  private readonly sameCameraScanCooldownMs = 1500;

  duplicateScanMessage = '';
  duplicateScanMessageTimeout?: ReturnType<typeof setTimeout>;

  constructor(
    private zone: NgZone,
    private translate: TranslateService
  ) { }

  get isCameraOpen(): boolean {
    return this.cameraStatus === ScannerCameraStatus.OPEN;
  }

  get showCameraState(): boolean {
    return this.cameraStatus !== ScannerCameraStatus.OPEN;
  }

  get showCameraStartGuide(): boolean {
    return this.cameraStatus === ScannerCameraStatus.IDLE
      || this.cameraStatus === ScannerCameraStatus.READY
      || this.cameraStatus === ScannerCameraStatus.PAUSED;
  }

  get showCameraLoader(): boolean {
    return this.cameraStatus === ScannerCameraStatus.INITIALIZING
      || this.cameraStatus === ScannerCameraStatus.OPENING;
  }

  get showCameraActionButton(): boolean {
    return this.cameraStatus === ScannerCameraStatus.CAMERA_BUSY;
  }

  get showManualEntryButton(): boolean {
    return this.cameraStatus === ScannerCameraStatus.PERMISSION_DENIED
      || this.cameraStatus === ScannerCameraStatus.CAMERA_NOT_FOUND
      || this.cameraStatus === ScannerCameraStatus.CAMERA_BUSY
      || this.cameraStatus === ScannerCameraStatus.ERROR;
  }

  get cameraActionButtonLabel(): string {
    if (this.cameraStatus === ScannerCameraStatus.CAMERA_BUSY) {
      return this.translate.instant('CAMERA_SCANNER.BTN_TRY_AGAIN');
    }

    return this.translate.instant('CAMERA_SCANNER.BTN_OPEN_CAMERA');
  }

  get cameraStatusTitle(): string {
    switch (this.cameraStatus) {
      case ScannerCameraStatus.INITIALIZING:
        return this.translate.instant('CAMERA_SCANNER.PREPARING_CAMERA');

      case ScannerCameraStatus.OPENING:
        return this.translate.instant('CAMERA_SCANNER.OPENING_CAMERA');

      case ScannerCameraStatus.PERMISSION_DENIED:
        return this.translate.instant('CAMERA_SCANNER.CAMERA_PERMISSION_BLOCKED');

      case ScannerCameraStatus.CAMERA_NOT_FOUND:
        return this.translate.instant('CAMERA_SCANNER.NO_CAMERA_FOUND');

      case ScannerCameraStatus.CAMERA_BUSY:
        return this.translate.instant('CAMERA_SCANNER.CAMERA_UNAVAILABLE');

      case ScannerCameraStatus.ERROR:
        return this.translate.instant('CAMERA_SCANNER.CAMERA_COULD_NOT_START');

      default:
        return '';
    }
  }

  get cameraStatusDescription(): string {
    switch (this.cameraStatus) {
      case ScannerCameraStatus.OPENING:
        return this.translate.instant('CAMERA_SCANNER.ALLOW_CAMERA_ACCESS');

      case ScannerCameraStatus.PERMISSION_DENIED:
        return this.translate.instant('CAMERA_SCANNER.CAMERA_ACCESS_BLOCKED_DESCRIPTION');

      case ScannerCameraStatus.CAMERA_NOT_FOUND:
        return this.translate.instant('CAMERA_SCANNER.NO_CAMERA_FOUND_DESCRIPTION');

      case ScannerCameraStatus.CAMERA_BUSY:
        return this.translate.instant('CAMERA_SCANNER.CAMERA_BUSY_DESCRIPTION');

      case ScannerCameraStatus.ERROR:
        return this.translate.instant('CAMERA_SCANNER.CAMERA_ERROR_DESCRIPTION');

      default:
        return '';
    }
  }

  get scannedItemsTotalRecords(): number {
    return this.scannedItems.length;
  }

  get paginatedScannedItems(): InventoryMultiScannerItem[] {
    const startIndex = this.scannedItemsPaginatorFirst;
    const endIndex = startIndex + this.scannedItemsPaginatorRows;

    return this.scannedItems.slice(startIndex, endIndex);
  }

  get scannedItemsPaginatorPageNumber(): number {
    const rows = this.scannedItemsPaginatorRows || 1;

    // convert local 0-based item offset to the 1-based paginator expected page number
    return Math.floor((this.scannedItemsPaginatorFirst ?? 0) / rows) + 1;
  }

  async ngAfterViewInit(): Promise<void> {
    this.drawerOpen = this.startDrawerOpen;
    this.hydrateInitialItems();
    this.setCameraStatus(ScannerCameraStatus.INITIALIZING);

    try {
      // console.log('DynamSoft scanner version', CaptureVisionRouterModule.getVersion());

      await this.initializeScanner();

      this.throwIfDestroyed();

      this.setCameraStatus(ScannerCameraStatus.READY);

      if (this.autoStart) {
        await this.openCamera();
      }
    } catch (error: any) {
      this.handleError(error);
    } finally {
      this.notifyScannerReady();
    }
  }

  private notifyScannerReady() {
    if (!this.markScannerAsReady) return;

    this.markScannerAsReady();
  }

  async startScanner(): Promise<void> {
    await this.scannerReady;

    if (this.bDestroyed) return;

    await this.openCamera();
  }

  async stopScanner(): Promise<void> {
    await this.scannerReady;
    await this.closeCamera(ScannerCameraStatus.PAUSED);

    if (this.bDestroyed) {
      return;
    }

    this.manualEntryOpen = false;
    this.manualSerialNumber = '';
    this.manualEntryError = '';
  }

  private async openCameraInternal(): Promise<void> {
    if (!this.cameraEnhancer || this.bDestroyed) return;

    if (this.cameraEnhancer.isOpen()) {
      this.setCameraStatus(ScannerCameraStatus.OPEN);
      return;
    }

    const permissionState = await this.getCameraPermissionState();

    if (this.bDestroyed) {
      return;
    }

    if (permissionState === 'denied') {
      this.setCameraStatus(ScannerCameraStatus.PERMISSION_DENIED);
      return;
    }

    this.setCameraStatus(ScannerCameraStatus.OPENING);

    try {
      await this.cameraEnhancer.open();

      if (this.bDestroyed) {
        return;
      }

      await this.startCapturing();

      if (this.bDestroyed) {
        return;
      }

      this.setCameraStatus(ScannerCameraStatus.OPEN);
    } catch (error: any) {
      if (this.bDestroyed) {
        return;
      }

      this.applyCameraOpenError(error);
    }
  }

  private async closeCameraInternal(nextStatus = ScannerCameraStatus.PAUSED): Promise<void> {
    try {
      await this.stopCapturing();

      if (this.cameraEnhancer?.isOpen()) {
        this.cameraEnhancer.close();
      }

      this.releaseBrowserCameraStream();
    } catch (error: any) {
      console.warn('Error closing scanner camera', error);
    } finally {
      if (!this.bDestroyed) {
        this.setCameraStatus(nextStatus);
      }
    }
  }

  async toggleCamera(): Promise<void> {
    if (this.cameraEnhancer?.isOpen()) {
      await this.closeCamera(ScannerCameraStatus.PAUSED);
    } else {
      await this.openCamera();
    }
  }

  async onCameraChange(event: Event): Promise<void> {
    const cameraId = (event.target as HTMLSelectElement).value;

    if (!cameraId || !this.cameraEnhancer) return;

    this.selectedCameraId = cameraId;

    try {
      await this.cameraEnhancer.selectCamera(cameraId);
    } catch (error: any) {
      this.handleError(error);
    }
  }

  toggleDrawer(): void {
    this.drawerOpen = !this.drawerOpen;

    if (!this.drawerOpen) {
      this.manualEntryOpen = false;
      this.manualSerialNumber = '';
      this.manualEntryError = '';
    }
  }

  openManualEntry(): void {
    this.manualEntryOpen = true;
    this.drawerOpen = true;
    this.manualEntryError = '';
  }

  cancelManualEntry(): void {
    this.manualEntryOpen = false;
    this.manualSerialNumber = '';
    this.manualEntryError = '';
  }

  addManualEntry(): void {
    const value = this.manualSerialNumber.trim();

    if (!value) return;

    const wasAdded = this.addScannedValue(value, 'manual');

    if (!wasAdded) return;

    this.manualSerialNumber = '';
    // this.manualEntryOpen = false;
    this.manualEntryError = '';
  }

  removeItem(item: InventoryMultiScannerItem): void {
    this.scannedItems = this.scannedItems.filter(
      scannedItem => scannedItem.id !== item.id
    );

    this.rebuildSeenSerialNumbers();
    this.normalizeScannedItemsPaginator();
    this.emitItemsChange();
  }

  clearItems(): void {
    this.scannedItems = [];
    this.seenSerialNumbers.clear();
    this.scannedItemsPaginatorFirst = 0;
    this.emitItemsChange();
  }

  async onCloseClick(): Promise<void> {
    await this.stopScanner();
    this.scannerClosed.emit();
  }

  async onFinishClick(): Promise<void> {
    await this.stopScanner();
    this.finishScanning.emit([...this.scannedItems]);
  }

  trackByItemId(_: number, item: InventoryMultiScannerItem): string {
    return item.id;
  }

  onScannedItemsPageChange(event: any): void {
    this.scannedItemsPaginatorFirst = event.first;
    this.scannedItemsPaginatorRows = event.rows;
  }

  private setCameraStatus(status: ScannerCameraStatus): void {
    this.cameraStatus = status;
  }

  private hydrateInitialItems(): void {
    const sanitizedInitialItems = (this.initialItems || [])
      .map(item => item.trim())
      .filter(Boolean);

    const initialItems = this.allowDuplicateScans ? sanitizedInitialItems : Array.from(new Set(sanitizedInitialItems));

    this.scannedItems = initialItems.map(serialNumber => ({
      id: this.createScanId(),
      serialNumber,
      scannedAt: new Date(),
      source: 'manual'
    }));

    this.scannedItemsPaginatorFirst = 0;
    this.rebuildSeenSerialNumbers();
    this.emitItemsChange();
  }

  private async initializeScanner(): Promise<void> {
    await initializeDynamsoft();
    this.throwIfDestroyed();

    this.cameraView = await CameraView.createInstance();
    this.throwIfDestroyed();

    this.cameraEnhancer = await CameraEnhancer.createInstance(this.cameraView);
    this.setupCameraEvents();

    const cameraUI = this.cameraView.getUIElement();

    cameraUI.querySelector('.dce-sel-camera')?.remove();
    cameraUI.querySelector('.dce-sel-resolution')?.remove();

    this.cameraView.setPowerByMessageVisible(false);

    await this.getAllCameras();

    this.throwIfDestroyed();

    if (!this.uiWrapper?.nativeElement) {
      throw new Error('Scanner UI wrapper was not found.');
    }

    this.uiWrapper.nativeElement.append(this.cameraView.getUIElement());

    this.cvRouter = await CaptureVisionRouter.createInstance();
    await this.setupRouterSettings();

    this.throwIfDestroyed();

    this.cvRouter.setInput(this.cameraEnhancer);

    await this.cvRouter.addResultReceiver({
      onDecodedBarcodesReceived: capturedData => {
        this.handleScannerResults(capturedData);
      }
    });

    await this.setupResultFilters();

    this.throwIfDestroyed();

    this.cameraView.setScanLaserVisible(false);
  }

  async getAllCameras(): Promise<any> {
    const camerasList = await this.cameraEnhancer?.getAllCameras();

    if (camerasList?.length) {
      this.camerasList = camerasList;
      this.selectedCameraId = this.selectedCameraId || camerasList[0]?.deviceId || '';
    }

    return camerasList;
  }

  private setupCameraEvents(): void {
    this.cameraEnhancer?.on('cameraOpen', () => {
      this.zone.run(() => {
        this.setCameraStatus(ScannerCameraStatus.OPEN);
      });
    });

    this.cameraEnhancer?.on('cameraClose', () => {
      this.zone.run(() => {
        if (!this.bDestroyed && this.cameraStatus === ScannerCameraStatus.OPEN) {
          this.setCameraStatus(ScannerCameraStatus.PAUSED);
        }
      });
    });
  }

  private async setupRouterSettings(): Promise<void> {
    if (!this.cvRouter) return;

    const settings = await this.cvRouter.getSimplifiedSettings(this.captureTemplateName);

    settings.barcodeSettings.barcodeFormatIds = this.barcodeFormatIds;

    await this.cvRouter.updateSettings(this.captureTemplateName, settings);
  }

  private async setupResultFilters(): Promise<void> {
    if (!this.cvRouter) return;

    const filter = new MultiFrameResultCrossFilter();

    filter.setDuplicateForgetTime('barcode', this.duplicateForgetTime);
    filter.enableResultCrossVerification('barcode', true);
    filter.enableResultDeduplication('barcode', false); // false -> allow angular handle duplicates to display message on the fly

    await this.cvRouter.addResultFilter(filter);
  }

  private async startCapturing(): Promise<void> {
    if (!this.cvRouter || this.isCapturing) return;

    await this.cvRouter.startCapturing(this.captureTemplateName);
    this.cameraView?.setScanLaserVisible(false);
    this.isCapturing = true;
  }

  private async stopCapturing(): Promise<void> {
    if (!this.cvRouter || !this.isCapturing) return;

    await this.cvRouter.stopCapturing();
    this.isCapturing = false;
  }

  private queueCameraOperation(operation: () => Promise<void>): Promise<void> {
    // allow next operation to run even if previous failed
    const nextOperation = this.pendingCameraOperation.then(
      operation,
      operation
    );

    this.pendingCameraOperation = nextOperation;

    return nextOperation;
  }

  private handleScannerResults(capturedData: any): void {
    const resultItems = capturedData?.barcodeResultItems || [];

    if (!resultItems.length) return;

    const serialNumbersFromCurrentFrame = new Set<string>();
    let addedAnyItem = false;

    this.zone.run(() => {
      resultItems.forEach((item: any) => {
        const serialNumber = String(item?.text || '').trim();

        if (!serialNumber) return;

        if (serialNumbersFromCurrentFrame.has(serialNumber)) return;

        serialNumbersFromCurrentFrame.add(serialNumber);

        // handle "already scanned" message when the item was scanned before
        const wasAdded = this.addScannedValue(
          serialNumber,
          'camera',
          item?.formatString || item?.format
        );

        addedAnyItem = addedAnyItem || wasAdded;
      });

      if (addedAnyItem) {
        this.playSuccessFeedback();
      }
    });
  }

  private addScannedValue(serialNumber: string, source: 'camera' | 'manual', format?: string): boolean {
    const value = serialNumber.trim();

    if (!value) {
      if (source === 'manual') {
        this.manualEntryError = this.translate.instant('CAMERA_SCANNER.MANUAL_ENTRY_REQUIRED');
      }

      return false;
    }

    if (this.maxItems !== undefined && this.scannedItems.length >= this.maxItems) {
      if (source === 'manual') {
        this.manualEntryError = this.translate.instant('CAMERA_SCANNER.MAX_ITEMS_REACHED', {
          maxItems: this.maxItems
        });
      }

      return false;
    }

    if (!this.allowDuplicateScans && this.seenSerialNumbers.has(value)) {
      this.duplicateScanned.emit(value);

      if (source === 'manual') {
        this.manualEntryError = this.translate.instant('CAMERA_SCANNER.DUPLICATE_SCAN');
      } else if (!this.isSameRecentCameraScan(value)) {

        // handle "duplicated warning msg" right after successful scan
        this.showDuplicateScanMessage();
      }

      return false;
    }

    const item: InventoryMultiScannerItem = {
      id: this.createScanId(),
      serialNumber: value,
      scannedAt: new Date(),
      source,
      format
    };

    this.seenSerialNumbers.add(value);
    this.scannedItems = [item, ...this.scannedItems];
    this.scannedItemsPaginatorFirst = 0;

    if (source === 'camera') {
      this.lastCameraScanValue = value;
      this.lastCameraScanAt = Date.now();
    }

    if (this.openDrawerOnScan) {
      this.drawerOpen = true;
    }

    this.itemScanned.emit(item);
    this.emitItemsChange();

    return true;
  }

  private rebuildSeenSerialNumbers(): void {
    this.seenSerialNumbers = new Set(
      this.scannedItems.map(item => item.serialNumber)
    );
  }

  private emitItemsChange(): void {
    this.itemsChange.emit([...this.scannedItems]);
  }

  private playSuccessFeedback(): void {
    try {
      Feedback.beep();
    } catch (error: any) {
      console.warn('Scanner beep feedback failed', error);
    }

    try {
      navigator.vibrate?.(80);
    } catch (error: any) {
      console.warn('Scanner vibration feedback failed', error);
    }
  }

  private async getCameraPermissionState(): Promise<string> {
    if (!navigator.permissions?.query) {
      return 'unsupported';
    }

    try {
      const permissionStatus = await navigator.permissions.query({
        name: 'camera' as any
      });

      return permissionStatus.state;
    } catch (_) {
      return 'unsupported';
    }
  }

  private applyCameraOpenError(error: any): void {
    const errorName = String(error?.name ?? '').toLowerCase();
    const errorMessage = String(error?.message ?? error ?? '').toLowerCase();
    const errorText = `${errorName} ${errorMessage}`;

    if (
      errorText.includes('notallowed') ||
      errorText.includes('permission') ||
      errorText.includes('denied')
    ) {
      this.setCameraStatus(ScannerCameraStatus.PERMISSION_DENIED);
      this.scannerError.emit(error);
      return;
    }

    if (
      errorText.includes('notfound') ||
      errorText.includes('device not found') ||
      errorText.includes('no camera')
    ) {
      this.setCameraStatus(ScannerCameraStatus.CAMERA_NOT_FOUND);
      this.scannerError.emit(error);
      return;
    }

    if (
      errorText.includes('notreadable') ||
      errorText.includes('could not start') ||
      errorText.includes('in use')
    ) {
      this.setCameraStatus(ScannerCameraStatus.CAMERA_BUSY);
      this.scannerError.emit(error);
      return;
    }

    this.setCameraStatus(ScannerCameraStatus.ERROR);
    this.scannerError.emit(error);
  }

  private isSameRecentCameraScan(serialNumber: string): boolean {
    const now = Date.now();

    return this.lastCameraScanValue === serialNumber && now - this.lastCameraScanAt < this.sameCameraScanCooldownMs;
  }

  private showDuplicateScanMessage(): void {
    if (this.duplicateScanMessage) return;

    this.duplicateScanMessage = this.translate.instant('CAMERA_SCANNER.DUPLICATE_SCAN');

    if (this.duplicateScanMessageTimeout) {
      clearTimeout(this.duplicateScanMessageTimeout);
    }

    this.duplicateScanMessageTimeout = setTimeout(() => {
      this.zone.run(() => {
        this.duplicateScanMessage = '';
      });
    }, 3000);
  }

  openCamera(): Promise<void> {
    return this.queueCameraOperation(() => {
      return this.openCameraInternal();
    });
  }

  closeCamera(nextStatus = ScannerCameraStatus.PAUSED): Promise<void> {
    return this.queueCameraOperation(() => {
      return this.closeCameraInternal(nextStatus);
    });
  }

  private releaseBrowserCameraStream(): void {
    const hostElement = this.uiWrapper?.nativeElement;
    const videos = Array.from(hostElement?.querySelectorAll('video') ?? []);

    // fallback cleanup for the browser MediaStream when the SDK keeps the video alive
    videos.forEach(video => {
      const stream = video.srcObject as MediaStream | null;

      stream?.getTracks().forEach(track => {
        track.stop();
      });

      video.pause();
      video.srcObject = null;
      video.removeAttribute('src');
      video.load();
    });
  }

  private normalizeScannedItemsPaginator(): void {
    if (!this.scannedItems.length) {
      this.scannedItemsPaginatorFirst = 0;
      return;
    }

    const lastPageIndex = Math.ceil(this.scannedItems.length / this.scannedItemsPaginatorRows) - 1;
    const maxFirstIndex = Math.max(0, lastPageIndex * this.scannedItemsPaginatorRows);

    if (this.scannedItemsPaginatorFirst > maxFirstIndex) {
      this.scannedItemsPaginatorFirst = maxFirstIndex;
    }
  }

  private throwIfDestroyed(): void {
    if (this.bDestroyed) {
      throw new Error(ERROR_DESTROYED);
    }
  }

  private async cleanupResources(): Promise<void> {
    try {
      await this.scannerReady;

      await this.closeCamera(ScannerCameraStatus.IDLE);

      this.cvRouter?.dispose();
      this.cameraEnhancer?.dispose();
    } catch (error: any) {
      if (error?.message === ERROR_DESTROYED) return;

      console.warn('Error cleaning up scanner resources', error);
    }
  }

  private handleError(error: any): void {
    if (error?.message === ERROR_DESTROYED) {
      console.log(ERROR_DESTROYED);
      return;
    }

    this.setCameraStatus(ScannerCameraStatus.ERROR);
    console.error(error?.message || error);

    this.scannerError.emit(error);
  }

  private createScanId(): string {
    // Used by inventories that allow scanning same SN multiple times
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  ngOnDestroy(): void {
    this.bDestroyed = true;

    if (this.duplicateScanMessageTimeout) {
      clearTimeout(this.duplicateScanMessageTimeout);
    }

    this.cleanupResources();
  }
}
